import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { useGetConfiguration } from './api/configuration';
import { useGetCompanyInformation } from './api/directory';
import { companyAtom } from './atoms/company';
import { FEATURE_FLAGS_UNAVAILABLE, featureFlagsAtom } from './atoms/featureFlags';
import { AmelioWrapperMock } from './components/AmelioWrapperMock/AmelioWrapperMock';
import { Locale, type LocaleType } from './constants/Locale';
import { useError } from './hooks/utils/useError';
import { RootNavigator } from './navigation/RootNavigator';
import { ScrollToTop } from './navigation/ScrollToTop';
import { RouteName } from './navigation/types';
import i18n, { browserLanguage } from './plugins/i18n';
import { setCompanyDefaultLanguage } from './utils/localization';

export interface AppProps {
    language?: LocaleType;
    /**
     * True when the Engagement host mounts this module, so the context every embedded-aware
     * screen reads is provided at the module's root rather than only under the profile entry
     * point. Navigation itself needs no flag: `useNavigate` offers every move to the host and
     * only pushes locally when nothing takes it.
     */
    embedded?: boolean;
}

export const isStandalone = import.meta.env.MODE === 'development';

const isSurveyAnswerRoute = () => window.location.pathname.startsWith(RouteName.SurveyAnswer);

export default function App({ language }: AppProps) {
    const { handleError } = useError();
    const isPublicSurveyRoute = isSurveyAnswerRoute();
    const { data: configData, error } = useGetConfiguration({ enabled: !isPublicSurveyRoute });
    const { data: company } = useGetCompanyInformation({ enabled: !isPublicSurveyRoute });

    const setFeatureFlags = useSetAtom(featureFlagsAtom);
    const setCompany = useSetAtom(companyAtom);

    useEffect(() => {
        i18n.changeLanguage(language || browserLanguage() || Locale.EN);
    }, [language]);

    useEffect(() => {
        if (error) {
            handleError({ error });
            // Unavailable, not empty: an empty list would read as "the backend told us nothing is
            // on" and every gated route would announce the feature is not enabled for this
            // organization. The gates offer a retry instead, and nothing spins forever.
            setFeatureFlags(FEATURE_FLAGS_UNAVAILABLE);
        }
        if (configData) {
            setFeatureFlags(configData.featureFlags ?? []);
        }
        if (company) {
            setCompany(company);
            if (company.company?.defaultLanguage) {
                setCompanyDefaultLanguage(company.company.defaultLanguage);
            }
        }
    }, [error, handleError, configData, setFeatureFlags, company, setCompany]);

    // if standalone render amelio wrapper
    if (isStandalone) {
        return (
            <div className={'app-container'}>
                <BrowserRouter data-testid={'browser-router'}>
                    <ScrollToTop />

                    <AmelioWrapperMock>
                        <RootNavigator data-testid={'root-navigator'} />
                    </AmelioWrapperMock>
                </BrowserRouter>
            </div>
        );
    }

    return (
        <div className={'app-container'}>
            <div className={'app-content'}>
                <BrowserRouter data-testid={'browser-router'}>
                    <ScrollToTop />

                    <RootNavigator data-testid={'root-navigator'} />
                </BrowserRouter>
            </div>
        </div>
    );
}
